import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { setPageTitle } from '../../../../redux/title/title.slice';
import { getAdvisorBookSummary } from '../../../../redux/aum/aum.slice';

import Loader from '../../../../components/ui/loader';
import { FTable, FTableCell } from '../../../../components/ui/table';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, useTheme } from '@mui/system';

function ViewAdvisorBook() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const theme = useTheme();

  const officeId = useSelector((state) => state.auth.user.tenant_id);

  const { advisorsAcrossProviders, advisorBookSummary, loadingAdvisorBookSummary, bookAnalysisUI } =
    useSelector((state) => state.aum);

  const [providerSort, setProviderSort] = useState({
    field: 'name',
    direction: 'asc'
  });

  // ✅ safe selected advisor lookup
  const selectedAdvisor = useMemo(() => {
    return (advisorsAcrossProviders || []).find((a) => a.id === id);
  }, [advisorsAcrossProviders, id]);

  const title = selectedAdvisor?.name || 'Advisor Summary';

  useEffect(() => {
    dispatch(setPageTitle(title));

    // ✅ SAFE fallback using UI state (not direct selectedAdvisor.asof)
    const fromDate = bookAnalysisUI?.isMonthEnd
      ? bookAnalysisUI?.selectedMonth
      : dayjs().format('YYYY-MM-DD');

    const payload = {
      officeId,
      partnerId: id,
      serviceProviderId: '',
      fromDate
    };

    dispatch(getAdvisorBookSummary(payload));
  }, [dispatch, id, officeId, title, bookAnalysisUI]);

  const handleSort = (setter, current, field) => {
    setter({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedProviders = [...(advisorBookSummary || [])].sort((a, b) => {
    const { field, direction } = providerSort;

    let result = 0;

    if (field === 'name') {
      result = (a.name || '').localeCompare(b.name || '');
    }

    if (field === 'sumSys') {
      result = (a.sumSys || 0) - (b.sumSys || 0);
    }

    return direction === 'asc' ? result : -result;
  });

  const SortIcon = ({ active, direction }) => {
    if (!active) {
      return <ArrowUpwardIcon fontSize="small" sx={{ opacity: 0.4 }} />;
    }

    return direction === 'asc' ? (
      <ArrowUpwardIcon fontSize="small" />
    ) : (
      <ArrowDownwardIcon fontSize="small" />
    );
  };

  if (loadingAdvisorBookSummary) {
    return <Loader loading={true} message="Loading Advisor Book..." />;
  }

  const columns = [
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setProviderSort, providerSort, 'name')}>
          Provider
          <SortIcon active={providerSort.field === 'name'} direction={providerSort.direction} />
        </Box>
      )
    },
    { label: 'Code' },
    {
      label: (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => handleSort(setProviderSort, providerSort, 'sumSys')}>
          Amount
          <SortIcon active={providerSort.field === 'sumSys'} direction={providerSort.direction} />
        </Box>
      )
    },
    { label: 'As Of' }
  ];

  return (
    <FTable
      title={title}
      columns={columns}
      rows={sortedProviders}
      emptyMessage="No data found"
      renderRow={(row) => (
        <>
          <FTableCell>{row.name || '—'}</FTableCell>

          <FTableCell>{row.code || '—'}</FTableCell>

          <FTableCell
            sx={{
              color: theme.palette.accent.main,
              fontWeight: 600
            }}>
            R {row.sumSys?.toLocaleString() ?? '—'}
          </FTableCell>

          <FTableCell>{row.asof ? dayjs(row.asof).format('YYYY-MM-DD') : '—'}</FTableCell>
        </>
      )}
    />
  );
}

export default ViewAdvisorBook;
